import { NetworkStatus, useQuery } from "@apollo/client";
import { Flex, LoadingSpinner, PageContainer, PageGridContainer } from "@govlaunch/web";
import Link from "next/link";
import { useRouter } from "next/router";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";
import StoriesPageSeo from "~/components/seo/StoriesPageSeo";
import { useIntlLocale } from "~/lib/withIntl";
import SearchStoriesQuery from "~/src/components/story/graphql/SearchStoriesQuery";
import {
  ISearchStoriesQuery,
  ISearchStoriesQueryVariables,
} from "~/src/components/story/graphql/__generated__/SearchStoriesQuery.generated";
import hidePodcastTag from "~/src/components/story/pages/hidePodcastTag";
import StoryCardWithPlaceholder from "~/src/components/story/StoryCardWithPlaceholder";
import StoryPageFilters from "~/src/components/story/StoryPageFilters";
import TranslationButton from "~/src/components/translation/TranslationButton";
import NoResultsFound from "~/src/shared/components/NoResultsFound";
import stringOrFirst from "~/src/utils/stringOrFirst";

export default function StoriesListingPage() {
  const router = useRouter();
  const { locale } = useIntlLocale();

  const variables: ISearchStoriesQueryVariables = {
    title: stringOrFirst(router.query.title),
    country: stringOrFirst(router.query.country),
    state: stringOrFirst(router.query.state),
    tags: Array.isArray(router.query.tags) ? router.query.tags : router.query.tags ? [router.query.tags] : [],
    cursor: null,
  };

  const { data, loading, fetchMore, networkStatus } = useQuery<ISearchStoriesQuery, ISearchStoriesQueryVariables>(
    SearchStoriesQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables,
    },
  );

  return (
    <div>
      <StoriesPageSeo />

      <StoryPageFilters />

      {loading && NetworkStatus.loading === networkStatus && (
        <Flex justify="center">
          <LoadingSpinner />
        </Flex>
      )}

      {data?.searchStories?.items?.length === 0 ? (
        <PageContainer>
          <NoResultsFound mt={32} />
        </PageContainer>
      ) : (
        <PageGridContainer columns={[1, 1, 2, 3]} spacingY={6}>
          {data?.searchStories?.items?.map((story, index) => {
            const translationLinkWrap = (slug: string) => {
              return (
                <TranslationButton
                  paddingY="4px"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    router.push(`/stories/${slug}?lang=${locale}`);
                  }}
                />
              );
            };

            return (
              <Link
                href="/stories/[storySlug]"
                as={`/stories/${story.slug}`}
                passHref={true}
                key={story._id}
                legacyBehavior={true}
              >
                <StoryCardWithPlaceholder
                  story={hidePodcastTag(story)}
                  data-intercom-target={index === 0 ? "First story" : undefined}
                  translationLinkWrap={locale !== "en-US" ? translationLinkWrap : undefined}
                />
              </Link>
            );
          })}
        </PageGridContainer>
      )}

      {data?.searchStories?.pageInfo?.hasNextPage && (
        <Flex justifyContent="center" mt={6}>
          <ShowMoreButtonWithIcon
            loading={loading || networkStatus === NetworkStatus.fetchMore}
            onClick={() => {
              fetchMore({
                variables: {
                  ...variables,
                  cursor: data?.searchStories?.pageInfo?.endCursor || null,
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                  return {
                    ...previousQueryResult,
                    searchStories: {
                      __typename: "Stories",
                      items: [
                        ...(previousQueryResult.searchStories?.items || []),
                        ...(fetchMoreResult?.searchStories?.items || []),
                      ],
                      pageInfo: fetchMoreResult?.searchStories?.pageInfo || null,
                    },
                  };
                },
              });
            }}
          />
        </Flex>
      )}
    </div>
  );
}
